// src/components/sales/pages/scheduled-messages/ScheduledMessageModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { XCircle, CheckCircle } from "lucide-react";
import ModalPortal from "../../../shared/ui/ModalPortal";

const CHANNEL_OPTIONS = [
  { value: "Email", labelKey: "sales.scheduledMessages.channels.email" },
  { value: "CUSTOM", labelKey: "sales.scheduledMessages.channels.other" },
];

const SCHEDULING_OPTIONS = [
  { value: "Immediate", labelKey: "sales.scheduledMessages.scheduling.immediate" },
  { value: "Scheduled", labelKey: "sales.scheduledMessages.scheduling.scheduled" }, // ✅ was "Schedule"
  { value: "Trigger", labelKey: "sales.scheduledMessages.scheduling.trigger" },
];

const TRIGGER_OPTIONS = [
  { value: "AfterSubmission", labelKey: "sales.scheduledMessages.triggers.afterSubmission" },
  { value: "AfterCall", labelKey: "sales.scheduledMessages.triggers.afterCall" },
  { value: "AfterReply", labelKey: "sales.scheduledMessages.triggers.afterReply" },
  { value: "CUSTOM", labelKey: "sales.scheduledMessages.triggers.other" },
];

const norm = (v) => String(v ?? "").trim();

const empty = () => ({
  clientId: "",
  templateId: "",
  schedulingType: "Immediate",
  scheduledLocal: "", // datetime-local
});

function toISO(localValue) {
  if (!localValue) return null;
  const d = new Date(localValue);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function fromISOToLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

export default function ScheduledMessageModal({
  mode = "create", // create | view
  initialData,
  clients,
  templates,
  loadingClients,
  loadingTemplates,
  onClose,
  onSubmit,
}) {
  const { t, i18n } = useTranslation();
  void i18n;
  const isView = mode === "view";
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(empty());

  // Channel select + optional custom
  const [channelSelect, setChannelSelect] = useState("Email");
  const [channelCustom, setChannelCustom] = useState("");

  // Trigger select + optional custom
  const [triggerSelect, setTriggerSelect] = useState("AfterSubmission");
  const [triggerCustom, setTriggerCustom] = useState("");

  const channelType = channelSelect === "CUSTOM" ? norm(channelCustom) : norm(channelSelect);
  const triggerType = triggerSelect === "CUSTOM" ? norm(triggerCustom) : norm(triggerSelect);

  const listClients = Array.isArray(clients) ? clients : [];
  const listTemplates = Array.isArray(templates) ? templates : [];

  const templateMap = useMemo(() => {
    const m = new Map();
    listTemplates.forEach((t) => {
      const id = t?.id ?? t?.templateId ?? t?.salesTemplateId;
      if (id != null) m.set(String(id), t);
    });
    return m;
  }, [listTemplates]);

  const selectedTemplate = useMemo(() => {
    if (!form.templateId) return null;
    return templateMap.get(String(form.templateId)) || null;
  }, [form.templateId, templateMap]);

  // filter templates by channel
  const filteredTemplates = useMemo(() => {
    const ch = norm(channelType).toLowerCase();
    if (!ch) return listTemplates;

    return listTemplates.filter((t) => {
      const tCh = norm(t?.channelType ?? t?.channel).toLowerCase();
      return tCh === ch;
    });
  }, [listTemplates, channelType]);

  useEffect(() => {
    // hydrate from initialData when view mode
    if (!initialData) return;

    setForm({
      clientId: String(initialData.clientId ?? ""),
      templateId: String(initialData.templateId ?? ""),
      schedulingType: initialData.schedulingType || "Immediate",
      scheduledLocal: initialData.scheduledDate ? fromISOToLocal(initialData.scheduledDate) : "",
    });

    // channel
    const ch = initialData.channelType || "";
    const known = ["Email"];
    const isKnown = known.includes(ch);
    setChannelSelect(isKnown ? ch : ch ? "CUSTOM" : "Email");
    setChannelCustom(!isKnown ? ch : "");

    // trigger
    const trg = initialData.triggerType || "";
    const knownTrg = ["AfterSubmission", "AfterCall", "AfterReply"];
    const isKnownTrg = knownTrg.includes(trg);
    setTriggerSelect(isKnownTrg ? trg : trg ? "CUSTOM" : "AfterSubmission");
    setTriggerCustom(!isKnownTrg ? trg : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  // Auto-fill channel from selected template if user didn't customize channel yet
  useEffect(() => {
    if (!selectedTemplate) return;
    const tCh = norm(selectedTemplate.channelType ?? selectedTemplate.channel);
    if (!tCh) return;

    // لو المستخدم لسه على default ومش كاتب custom — نسينك القناة على قناة التمبليت
    if (channelSelect !== "CUSTOM") {
      const known = ["Email"];
      if (known.includes(tCh)) {
        setChannelSelect(tCh);
      } else {
        setChannelSelect("CUSTOM");
        setChannelCustom(tCh);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  const title = useMemo(() => {
    if (isView) return t("sales.scheduledMessages.modal.viewTitle");
    return t("sales.scheduledMessages.modal.createTitle");
  }, [isView, t]);

  const validate = () => {
    if (!norm(form.clientId)) return t("sales.scheduledMessages.validation.clientRequired");
    if (!norm(form.templateId)) return t("sales.scheduledMessages.validation.templateRequired");
    if (!norm(channelType)) return t("sales.scheduledMessages.validation.channelRequired");
    if (!norm(form.schedulingType))
      return t("sales.scheduledMessages.validation.schedulingRequired");

    const clientId = parseInt(form.clientId, 10);
    const templateId = parseInt(form.templateId, 10);
    if (!Number.isFinite(clientId)) return t("sales.scheduledMessages.validation.invalidClient");
    if (!Number.isFinite(templateId))
      return t("sales.scheduledMessages.validation.invalidTemplate");

    if (form.schedulingType === "Scheduled") {
      if (!norm(form.scheduledLocal))
        return t("sales.scheduledMessages.validation.scheduledDateRequired");
      if (!toISO(form.scheduledLocal))
        return t("sales.scheduledMessages.validation.invalidScheduledDate");
    }

    if (form.schedulingType === "Trigger") {
      if (!norm(triggerType)) return t("sales.scheduledMessages.validation.triggerRequired");
    }

    return "";
  };

  const submit = async () => {
    const err = validate();
    if (err) return alert(err);

    const clientId = parseInt(form.clientId, 10);
    const templateId = parseInt(form.templateId, 10);

    const nowIso = new Date().toISOString();

    const payload = {
      clientId,
      templateId,
      channelType,
      schedulingType: form.schedulingType,
      scheduledDate:
        form.schedulingType === "Scheduled"
          ? toISO(form.scheduledLocal)
          : form.schedulingType === "Immediate"
            ? nowIso
            : null,
      triggerType: form.schedulingType === "Trigger" ? triggerType : null,
    };

    setSaving(true);
    try {
      await onSubmit?.(payload);
    } finally {
      setSaving(false);
    }
  };

  const mismatchWarning =
    selectedTemplate &&
    norm(selectedTemplate.channelType ?? selectedTemplate.channel) &&
    norm(selectedTemplate.channelType ?? selectedTemplate.channel).toLowerCase() !==
      norm(channelType).toLowerCase();

  return (
    <ModalPortal onClose={saving ? undefined : onClose}>
      <div className="fixed inset-0 z-[9999]">
        {/* Overlay */}
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-md"
          onMouseDown={saving ? undefined : onClose}
        />

        {/* Modal */}
        <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
          <div
            className="relative w-full max-w-5xl my-10 bg-white/10 backdrop-blur-2xl border border-white/20 shadow-[0_0_40px_rgba(255,0,255,0.30)] rounded-2xl overflow-hidden"
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            {/* Header */}
            <div className="p-6 bg-gradient-to-r from-pink-600 to-purple-700 flex justify-between items-center">
              <div className="min-w-0">
                <h2 className="text-2xl font-bold text-white">{title}</h2>
                {!isView && (
                  <p className="text-sm text-white/80 mt-1">
                    {t("sales.scheduledMessages.modal.subtitle")}
                  </p>
                )}
              </div>

              <button
                onClick={saving ? undefined : onClose}
                disabled={saving}
                className="text-white hover:text-gray-200 disabled:opacity-50"
                aria-label={t("common.close")}
                type="button"
                title={t("common.close")}
              >
                <XCircle className="w-7 h-7" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 max-h-[72vh] overflow-y-auto text-white">
              <div className="grid md:grid-cols-2 gap-4">
                {/* Client */}
                <Box label={t("sales.scheduledMessages.modal.clientLabel")}>
                  <select
                    value={form.clientId}
                    onChange={(e) => setForm((p) => ({ ...p, clientId: e.target.value }))}
                    className={inputCls}
                    disabled={saving || isView}
                  >
                    <option value="" className="text-black">
                      {loadingClients
                        ? t("sales.scheduledMessages.modal.loadingClients")
                        : t("sales.scheduledMessages.modal.selectClient")}
                    </option>
                    {listClients.map((c) => (
                      <option key={String(c.id)} value={String(c.id)} className="text-black">
                        {(c.companyName || t("sales.scheduledMessages.modal.clientFallback")) +
                          (c.firstName || c.secondName
                            ? ` — ${(c.firstName || "")} ${(c.secondName || "")}`
                            : "")}
                      </option>
                    ))}
                  </select>
                </Box>

                {/* Channel */}
                <Box label={t("sales.scheduledMessages.modal.channelLabel")}>
                  <select
                    value={channelSelect}
                    onChange={(e) => setChannelSelect(e.target.value)}
                    className={inputCls}
                    disabled={saving || isView}
                  >
                    {CHANNEL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value} className="text-black">
                        {t(o.labelKey)}
                      </option>
                    ))}
                  </select>

                  {channelSelect === "CUSTOM" && (
                    <input
                      value={channelCustom}
                      onChange={(e) => setChannelCustom(e.target.value)}
                      className={inputCls + " mt-2"}
                      placeholder={t("sales.scheduledMessages.modal.channelCustomPlaceholder")}
                      disabled={saving || isView}
                    />
                  )}
                </Box>

                {/* Template */}
                <Box label={t("sales.scheduledMessages.modal.templateLabel")}>
                  <select
                    value={form.templateId}
                    onChange={(e) => setForm((p) => ({ ...p, templateId: e.target.value }))}
                    className={inputCls}
                    disabled={saving || isView}
                  >
                    <option value="" className="text-black">
                      {loadingTemplates
                        ? t("sales.scheduledMessages.modal.loadingTemplates")
                        : t("sales.scheduledMessages.modal.selectTemplate")}
                    </option>
                    {(filteredTemplates.length ? filteredTemplates : listTemplates).map((t) => {
                      const id = t?.id ?? t?.templateId ?? t?.salesTemplateId;
                      const label =
                        t?.templateName ||
                        t?.name ||
                        t("sales.scheduledMessages.modal.templateNumber", { id });
                      const ch = t?.channelType ?? t?.channel ?? "";
                      return (
                        <option key={String(id)} value={String(id)} className="text-black">
                          {label}
                          {ch ? ` — ${ch}` : ""}
                        </option>
                      );
                    })}
                  </select>

                  {mismatchWarning && (
                    <p className="mt-2 text-xs text-amber-200/90">
                      {t("sales.scheduledMessages.modal.templateChannelMismatch")}
                    </p>
                  )}
                </Box>

                {/* Scheduling */}
                <Box label={t("sales.scheduledMessages.modal.schedulingLabel")}>
                  <select
                    value={form.schedulingType}
                    onChange={(e) => setForm((p) => ({ ...p, schedulingType: e.target.value }))}
                    className={inputCls}
                    disabled={saving || isView}
                  >
                    {SCHEDULING_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value} className="text-black">
                        {t(o.labelKey)}
                      </option>
                    ))}
                  </select>
                </Box>

                {/* Scheduled Date */}
                {form.schedulingType === "Scheduled" && (
                  <Box label={t("sales.scheduledMessages.modal.scheduledDateLabel")}>
                    <input
                      type="datetime-local"
                      value={form.scheduledLocal}
                      onChange={(e) => setForm((p) => ({ ...p, scheduledLocal: e.target.value }))}
                      className={inputCls}
                      disabled={saving || isView}
                    />
                    <p className="mt-2 text-xs text-white/70">
                      {t("sales.scheduledMessages.modal.scheduledDateHint")}
                    </p>
                  </Box>
                )}

                {/* Trigger */}
                {form.schedulingType === "Trigger" && (
                  <Box label={t("sales.scheduledMessages.modal.triggerLabel")}>
                    <select
                      value={triggerSelect}
                      onChange={(e) => setTriggerSelect(e.target.value)}
                      className={inputCls}
                      disabled={saving || isView}
                    >
                      {TRIGGER_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value} className="text-black">
                          {t(o.labelKey)}
                        </option>
                      ))}
                    </select>

                    {triggerSelect === "CUSTOM" && (
                      <input
                        value={triggerCustom}
                        onChange={(e) => setTriggerCustom(e.target.value)}
                        className={inputCls + " mt-2"}
                        placeholder={t("sales.scheduledMessages.modal.triggerPlaceholder")}
                        disabled={saving || isView}
                      />
                    )}
                  </Box>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-4 mt-6">
                <button
                  onClick={saving ? undefined : onClose}
                  disabled={saving}
                  className="flex-1 py-3 rounded-xl font-semibold bg-gray-600 hover:bg-gray-700 text-white disabled:opacity-40"
                  type="button"
                >
                  {t("common.close")}
                </button>

                {!isView && (
                  <button
                    onClick={submit}
                    disabled={saving}
                    className="flex-1 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-pink-500 to-red-500 shadow-lg hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-2"
                    type="button"
                  >
                    <CheckCircle className="w-5 h-5" />
                    {saving
                      ? t("sales.scheduledMessages.saving")
                      : t("sales.scheduledMessages.actions.create")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

function Box({ label, children }) {
  return (
    <div className="bg-white/10 border border-white/20 rounded-xl p-4 backdrop-blur-xl">
      <label className="text-sm text-gray-200">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

const inputCls =
  "w-full px-3 py-2 bg-white/10 text-white border border-white/20 rounded-lg outline-none focus:ring-2 focus:ring-pink-400/30";
